import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEvent,
  Ref,
  ReactElement,
  ReactNode,
} from 'react';
import { isShortLabel } from './label.js';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'compact' | 'icon';

interface ButtonOwnProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

export type ButtonAsButtonProps = ButtonOwnProps
  & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps>
  & {
    as?: 'button';
    ref?: Ref<HTMLButtonElement>;
    href?: never;
    target?: never;
    rel?: never;
  };

export type ButtonAsAnchorProps = ButtonOwnProps
  & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps>
  & {
    as: 'a';
    ref?: Ref<HTMLAnchorElement>;
    disabled?: boolean;
  };

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

/** Plain-text labels up to this length are set in the pixel display face (md/sm only). */
const DISPLAY_LABEL_MAX = 12;

function buttonClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  children: ReactNode,
  className: string | undefined,
): string {
  return [
    'cards-kit-button',
    `cards-kit-button--${variant}`,
    `cards-kit-button--${size}`,
    // compact is a dense reading-face size and icon holds a glyph: neither takes
    // the pixel display face for its label (02 §2.1).
    (size === 'md' || size === 'sm') && isShortLabel(children, DISPLAY_LABEL_MAX) ? 'cards-kit-button--display' : undefined,
    className,
  ].filter(Boolean).join(' ');
}

export function Button(props: ButtonProps): ReactElement {
  const {
    as = 'button',
    children,
    variant = 'primary',
    size = 'md',
    className,
    ...rest
  } = props;
  const resolvedClassName = buttonClassName(variant, size, children, className);

  if (as === 'a') {
    const {
      disabled = false,
      href,
      onClick,
      tabIndex,
      ...anchorProps
    } = rest as Omit<ButtonAsAnchorProps, keyof ButtonOwnProps | 'as'>;
    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };
    return (
      <a
        {...anchorProps}
        className={resolvedClassName}
        href={disabled ? undefined : href}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : tabIndex}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      {...rest as Omit<ButtonAsButtonProps, keyof ButtonOwnProps | 'as'>}
      className={resolvedClassName}
    >
      {children}
    </button>
  );
}
